/**
 * Newsletter Section
 * Email signup form with customizable text
 */

import React, { useState } from 'react';
import useDarkModeNeutrals from '../../hooks/useDarkModeNeutrals';
import './sections.css';

function Newsletter({ settings = {}, content = {}, globalStyles = {}, store }) {
  const neutrals = useDarkModeNeutrals();
  const {
    layout = 'horizontal', // horizontal, stacked
    backgroundColor = neutrals.surfaceAlt,
    // Granular style settings — neutrals flip in dark mode.
    titleColor = neutrals.text,
    descriptionColor = neutrals.textMuted,
    inputBgColor = neutrals.surface,
    inputBorderColor = neutrals.borderStrong,
    inputTextColor = neutrals.text,
    inputRadius = '8px',
    buttonBgColor,
    buttonTextColor = '#ffffff',
    // Pill CTA default — matches HeroBanner/Slider M1 update.
    buttonRadius = '999px',
  } = settings;

  const {
    title = 'Stay Updated',
    description = 'Subscribe to get special offers and updates',
    buttonText = 'Subscribe',
    placeholderText = 'Enter your email',
  } = content;

  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [message, setMessage] = useState('');

  const colors = globalStyles.colors || {};
  const fonts = globalStyles.fonts || {};

  // Use settings colors or fall back to globalStyles
  const effectiveButtonBgColor = buttonBgColor || colors.primary || '#2563eb';

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !email.includes('@')) {
      setStatus('error');
      setMessage('Please enter a valid email address');
      return;
    }

    setStatus('loading');

    try {
      const res = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          storeSlug: store?.slug,
        }),
      });

      const data = await res.json();

      if (data.success) {
        setStatus('success');
        setMessage('Thank you for subscribing!');
        setEmail('');
      } else {
        setStatus('error');
        setMessage(data.error || 'Something went wrong');
      }
    } catch (err) {
      setStatus('error');
      setMessage('Failed to subscribe. Please try again.');
    }
  };

  const sectionStyle = {
    backgroundColor,
  };

  const titleStyle = {
    fontFamily: fonts.heading || 'inherit',
    color: titleColor,
  };

  const descriptionStyle = {
    color: descriptionColor,
  };

  const inputStyle = {
    backgroundColor: inputBgColor,
    borderColor: inputBorderColor,
    color: inputTextColor,
    borderRadius: inputRadius,
  };

  const buttonStyle = {
    backgroundColor: effectiveButtonBgColor,
    color: buttonTextColor,
    borderRadius: buttonRadius,
  };

  return (
    <section className="ts-newsletter" style={sectionStyle}>
      <div className="ts-newsletter__container">
        <div className={`ts-newsletter__content ts-newsletter__content--${layout}`}>
          <div className="ts-newsletter__text">
            {title && (
              <h2 className="ts-newsletter__title" style={titleStyle}>
                {title}
              </h2>
            )}
            {description && (
              <p className="ts-newsletter__description" style={descriptionStyle}>{description}</p>
            )}
          </div>

          <form className="ts-newsletter__form" onSubmit={handleSubmit}>
            <div className="ts-newsletter__input-group">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={placeholderText}
                className="ts-newsletter__input"
                style={inputStyle}
                disabled={status === 'loading'}
              />
              <button
                type="submit"
                className="ts-newsletter__button"
                style={buttonStyle}
                disabled={status === 'loading'}
              >
                {status === 'loading' ? (
                  <span className="ts-newsletter__spinner" />
                ) : (
                  buttonText
                )}
              </button>
            </div>

            {message && (
              <p className={`ts-newsletter__message ts-newsletter__message--${status}`}>
                {message}
              </p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}

export default React.memo(Newsletter);
